import axios from 'axios';
import {
  confirmPendingSchoolCheckout,
  confirmSchoolCheckoutSession,
  getSchoolSubscriptionSummary,
} from './api/billing.client';
import { normalizeSubscriptionStatus } from './subscriptionDisplay';

export const BILLING_ACTIVATION_PENDING_KEY = 'billing_activation_pending_school';

const POLL_MS = 2000;
const WEBHOOK_POLL_ATTEMPTS = 45;
const EXTENDED_POLL_ATTEMPTS = 30;

export function markBillingActivationPending(schoolId: string): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(BILLING_ACTIVATION_PENDING_KEY, schoolId);
}

export function clearBillingActivationPending(schoolId?: string): void {
  if (typeof sessionStorage === 'undefined') return;
  const current = sessionStorage.getItem(BILLING_ACTIVATION_PENDING_KEY);
  if (!schoolId || current === schoolId) {
    sessionStorage.removeItem(BILLING_ACTIVATION_PENDING_KEY);
  }
}

export function isBillingActivationPending(schoolId: string): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(BILLING_ACTIVATION_PENDING_KEY) === schoolId;
}

function isConfirmFallbackDisabledError(error: unknown): boolean {
  if (!axios.isAxiosError(error) || error.response?.status !== 403) return false;
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' && detail.toLowerCase().includes('checkout confirm is disabled');
}

async function pollUntilActive(schoolId: string, maxAttempts: number): Promise<boolean> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const summary = await getSchoolSubscriptionSummary(schoolId);
    if (normalizeSubscriptionStatus(summary.status) === 'active') {
      clearBillingActivationPending(schoolId);
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, POLL_MS));
  }
  return false;
}

/** Wait for Stripe webhook activation; optionally use server confirm fallback when enabled. */
export async function resolveCheckoutActivation(
  schoolId: string,
  sessionId: string | null,
  confirmFallbackEnabled: boolean
): Promise<'active' | 'pending'> {
  markBillingActivationPending(schoolId);

  if (await pollUntilActive(schoolId, WEBHOOK_POLL_ATTEMPTS)) {
    return 'active';
  }

  if (confirmFallbackEnabled) {
    try {
      if (sessionId) {
        await confirmSchoolCheckoutSession({ school_id: schoolId, session_id: sessionId });
      } else {
        await confirmPendingSchoolCheckout(schoolId);
      }
      if (await pollUntilActive(schoolId, 5)) {
        return 'active';
      }
      return 'active';
    } catch (error) {
      if (!isConfirmFallbackDisabledError(error)) {
        throw error;
      }
    }
  }

  if (await pollUntilActive(schoolId, EXTENDED_POLL_ATTEMPTS)) {
    return 'active';
  }

  return 'pending';
}
